/** Models for the hour classification template (malli) endpoints, api/WorkClassificationTemplates. */

export interface WorkClassificationTemplate {
  id: number;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  businessAreaHint: string | null;
  nodeCount: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface WorkClassificationTemplateNode {
  id: number;
  parentId: number | null;
  name: string;
  description: string | null;
  sortOrder: number;
}

export interface WorkClassificationTemplateDetail {
  template: WorkClassificationTemplate;
  nodes: WorkClassificationTemplateNode[];
}

export interface WorkClassificationTemplateInput {
  name: string;
  description: string | null;
  businessAreaHint: string | null;
}

export interface WorkClassificationTemplateNodeInput {
  parentId: number | null;
  name: string;
  description: string | null;
  sortOrder?: number;
}

export interface ApplyTemplateResult {
  applied: boolean;
  /** For example "Applied" or "TargetNotEmpty". */
  status: string;
  nodesCreated: number;
  nodesRemoved: number;
  targetNodes: number;
}

export interface PathRef {
  id: number;
  path: string;
}

export interface LinkMatch {
  projectNodeId: number;
  templateNodeId: number;
  path: string;
}

export interface LinkPreview {
  templateId: number;
  projectId: number;
  matches: LinkMatch[];
  unmatchedProjectNodes: PathRef[];
  unmatchedTemplateNodes: PathRef[];
  ambiguousPaths: string[];
}

export interface SyncAddition {
  templateNodeId: number;
  name: string;
  path: string;
  parentProjectNodeId: number | null;
  parentWillBeAdded: boolean;
  /** Not in the current backend response; used when present, otherwise the path prefix identifies the parent. */
  parentTemplateNodeId?: number | null;
}

export interface SyncRename {
  projectNodeId: number;
  templateNodeId: number;
  currentName: string;
  newName: string;
  path: string;
  entryCount: number;
  billedEntryCount: number;
}

export interface SyncDeletedInProject {
  templateNodeId: number;
  name: string;
  path: string;
}

export interface SyncLinkInstead {
  templateNodeId: number;
  path: string;
  projectNodeId: number;
}

export interface SyncPreview {
  templateId: number;
  projectId: number;
  additions: SyncAddition[];
  renames: SyncRename[];
  deletedInProject: SyncDeletedInProject[];
  linkInstead: SyncLinkInstead[];
  /** Informational only; never sent back. Count or list, depending on the backend shape. */
  projectOnlyNodes: number | unknown[];
  orphanedLinks: number | unknown[];
}

export interface SyncRequest {
  addTemplateNodeIds: number[];
  renameProjectNodeIds: number[];
  confirmBilledRenames: boolean;
}

export interface SyncResult {
  added: number;
  renamed: number;
  billedRenamesApplied: number;
  warning: string | null;
}

/** Optional initial classification choice sent with POST api/Projects. */
export interface ClassificationSetup {
  templateId?: number;
  copyFromProjectId?: number;
}

export const BILLED_RENAMES_WARNING = 'BILLED_RENAMES_MAY_CREATE_CORRECTIONS';
